/**
 * A rotina agendada no bloco "Começa quando": em que dias, a que horas e em que
 * fuso ela roda, tudo escolhido. Sai a agenda que o serviço lê (os horários da
 * semana, no fuso), e o resumo numa frase: "De segunda a sexta às 08:00".
 */

import { useState, type ReactNode } from 'react';
import { Botao, Campo, CampoSelecao, Icone } from '@/shared/ui';
import {
  DIAS_DA_SEMANA,
  FUSOS,
  agendaDoFormulario,
  formularioDaAgenda,
  problemasDaAgenda,
  resumoDaAgenda,
  type FormularioDaAgenda,
} from '../lib/inicio';
import type { Configuracao } from '../lib/edicao';

type Agenda = Extract<Configuracao['gatilho'], { tipo: 'agenda' }>;

export function AgendaDoInicio({
  agenda,
  aoMudar,
}: {
  agenda: Agenda;
  aoMudar: (agenda: Agenda) => void;
}): ReactNode {
  /*
   * O formulário fica aqui: um horário pela metade ("08:") ainda não é horário,
   * e sumiria se a tela só mostrasse a agenda. A agenda que sai leva só os
   * horários completos; o problema diz o que falta.
   */
  const [f, definirF] = useState<FormularioDaAgenda>(() => formularioDaAgenda(agenda));
  const mudar = (novo: FormularioDaAgenda): void => {
    definirF(novo);
    aoMudar(agendaDoFormulario(novo));
  };
  const problemas = problemasDaAgenda(f);
  const diasUteis =
    f.dias !== null && f.dias.length === 5 && [1, 2, 3, 4, 5].every((d) => f.dias!.includes(d));
  const fusos = FUSOS.some((z) => z.valor === f.fuso)
    ? FUSOS
    : [...FUSOS, { valor: f.fuso, rotulo: f.fuso }];

  return (
    <fieldset className="editor-do-mapa__condicao">
      <legend className="editor-do-mapa__condicao-titulo">Quando a rotina roda</legend>

      <div className="editor-do-mapa__valores" role="group" aria-label="Em que dias">
        <Botao
          variante={f.dias === null ? 'primario' : 'secundario'}
          miudo
          aria-pressed={f.dias === null}
          onClick={() => mudar({ ...f, dias: null })}
        >
          Todo dia
        </Botao>
        <Botao
          variante={diasUteis ? 'primario' : 'secundario'}
          miudo
          aria-pressed={diasUteis}
          onClick={() => mudar({ ...f, dias: [1, 2, 3, 4, 5] })}
        >
          Dias úteis
        </Botao>
      </div>
      <div className="editor-do-mapa__valores" role="group" aria-label="Os dias da semana">
        {DIAS_DA_SEMANA.map((d) => {
          const marcado = f.dias === null || f.dias.includes(d.dia);
          return (
            <Botao
              key={d.dia}
              variante={marcado ? 'primario' : 'secundario'}
              miudo
              aria-pressed={marcado}
              aria-label={d.nome}
              title={d.nome}
              onClick={() => {
                const atuais = f.dias ?? DIAS_DA_SEMANA.map((x) => x.dia);
                const dias = marcado ? atuais.filter((x) => x !== d.dia) : [...atuais, d.dia];
                mudar({ ...f, dias: dias.length === 7 ? null : [...dias].sort() });
              }}
            >
              {d.curto}
            </Botao>
          );
        })}
      </div>

      {f.horas.map((hora, i) => (
        <div key={i} className="editor-do-mapa__regra">
          <Campo
            rotulo={f.horas.length === 1 ? 'Horário' : `Horário ${i + 1}`}
            type="time"
            value={hora}
            onChange={(e) =>
              mudar({ ...f, horas: f.horas.map((h, j) => (j === i ? e.target.value : h)) })
            }
          />
          {f.horas.length > 1 && (
            <Botao
              variante="fantasma"
              icone
              miudo
              rotulo={`Tirar o horário ${i + 1}`}
              aria-label={`Tirar o horário ${i + 1}`}
              onClick={() => mudar({ ...f, horas: f.horas.filter((_, j) => j !== i) })}
            >
              <Icone nome="lixeira" />
            </Botao>
          )}
        </div>
      ))}
      <div className="editor-do-mapa__condicao-acoes">
        <Botao
          variante="secundario"
          miudo
          onClick={() => mudar({ ...f, horas: [...f.horas, '12:00'] })}
        >
          <Icone nome="mais" />
          Adicionar horário
        </Botao>
      </div>

      <CampoSelecao
        rotulo="Fuso"
        value={f.fuso}
        onChange={(e) => mudar({ ...f, fuso: e.target.value })}
      >
        {fusos.map((z) => (
          <option key={z.valor} value={z.valor}>
            {z.rotulo}
          </option>
        ))}
      </CampoSelecao>

      {problemas.length > 0 ? (
        <ul className="editor-do-mapa__pendencias">
          {problemas.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      ) : (
        <p className="editor-do-mapa__painel-ok">Roda: {resumoDaAgenda(agendaDoFormulario(f))}.</p>
      )}
    </fieldset>
  );
}
